import React, { useState } from 'react';
import { Container, Box, Typography, TextField, Button, MenuItem } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePatient } from '../context/patientContext';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import "../styles/signup.css";
import { useTranslation } from 'react-i18next';

function Signup() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { patientDraft, updateDemographics } = usePatient();

  const [phone, setPhone] = useState(patientDraft?.demographics.phone ?? '');
  const [zip, setZip] = useState(patientDraft?.demographics.zip ?? '');
  const [stateAbbr, setStateAbbr] = useState(patientDraft?.demographics.state ?? '');

  const handleZipChange = (e) => {
    // keep only digits, cap at 5
    const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZip(digits);
  };

  const handleStateChange = (e) => {
    // keep letters only, uppercase, cap at 2
    const val = e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2);
    setStateAbbr(val);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const payload = Object.fromEntries(formData.entries());


    const dob = formData.get('dob');

    // simple validations
    if (new Date(dob) > new Date()) {
      alert(t('signup.errors.dobFuture'));
      return;
    }
    if (!/^\d{5}$/.test(zip)) {
      alert(t('signup.errors.zipDigits'));
      return;
    }
    if (!/^[A-Z]{2}$/.test(stateAbbr)) {
      alert(t('signup.errors.stateAbbr'));
      return;
    }

    const patientDemographics = {
      first_name: payload.first_name,
      middle_name: payload.middle_name,
      last_name: payload.last_name,
      street_address: payload.street_address,
      city: payload.city,
      state: payload.state,
      zip: payload.zip,
      dob: payload.dob,
      phone: phone,
      sex: payload.sex,
      race: payload.race,
      highest_education_level: payload.highest_education_level,
    };
    localStorage.setItem('patientZip', zip);
    updateDemographics(patientDemographics);

    navigate('/testFlow', { state: { from: location.pathname, step: 'questionnaire' } });
  }

  return (
    <Container maxWidth="xs">
      <Box className="signup">
        <Typography component="h1" variant="h5">
          {t('signup.title')}
        </Typography>

        <Box component="form" autoComplete="off" onSubmit={handleSubmit} className="signup__form">
          <TextField
            margin="normal"
            required
            fullWidth
            label={t('signup.firstName')}
            name="first_name"
            data-cy="first_name-cy"
            defaultValue={patientDraft?.demographics.first_name ?? ''}
            autoFocus
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label={t('signup.lastName')}
            name="last_name"
            data-cy="last_name-cy"
            defaultValue={patientDraft?.demographics.last_name ?? ''}
          />
          <TextField
            margin="normal"
            fullWidth
            label={t('signup.middleName')}
            name="middle_name"
            data-cy="middle_name-cy"
            defaultValue={patientDraft?.demographics.middle_name ?? ''}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            label={t('signup.dob')}
            name="dob"
            type="date"
            data-cy="date_of_birth-cy"
            defaultValue={patientDraft?.demographics.dob ?? ''}
            slotProps={{
              htmlInput: { max: new Date().toISOString().split('T')[0] }, // disables future dates
              inputLabel: { shrink: true }
            }}
          />

          <TextField
            select
            margin="normal"
            required
            fullWidth
            label={t('signup.sex')}
            name="sex"
            data-cy="sex-cy"
            defaultValue={patientDraft?.demographics.sex ?? ''}
          >
            <MenuItem value="">{t('signup.select')}</MenuItem>
            <MenuItem value="1">{t('signup.sexOptions.male')}</MenuItem>
            <MenuItem value="2">{t('signup.sexOptions.female')}</MenuItem>
            <MenuItem value="3">{t('signup.sexOptions.transMale')}</MenuItem>
            <MenuItem value="4">{t('signup.sexOptions.transFemale')}</MenuItem>
          </TextField>

          <TextField
            select
            margin="normal"
            required
            fullWidth
            label={t('signup.raceEthnicity')}
            name="race"
            data-cy="race-cy"
            defaultValue={patientDraft?.demographics.race ?? ''}
          >
            <MenuItem value="">{t('signup.select')}</MenuItem>
            <MenuItem value="1">{t('signup.raceOptions.white')}</MenuItem>
            <MenuItem value="2">{t('signup.raceOptions.blackAfricanAmerican')}</MenuItem>
            <MenuItem value="3">{t('signup.raceOptions.hispanicLatinAmerican')}</MenuItem>
            <MenuItem value="4">{t('signup.raceOptions.nativeIndigenous')}</MenuItem>
            <MenuItem value="5">{t('signup.raceOptions.asian')}</MenuItem>
            <MenuItem value="6">{t('signup.raceOptions.nativeHawaiianPacificIslander')}</MenuItem>
          </TextField>

          <TextField
            select
            margin="normal"
            required
            fullWidth
            label={t('signup.highestEducationLevel')}
            name="highest_education_level"
            data-cy="highest_education_level-cy"
            defaultValue={patientDraft?.demographics.highest_education_level ?? ''}
          >
            <MenuItem value="">{t('signup.select')}</MenuItem>
            <MenuItem value="1">{t('signup.educationOptions.lessThanHighSchool')}</MenuItem>
            <MenuItem value="2">{t('signup.educationOptions.highSchoolGed')}</MenuItem>
            <MenuItem value="3">{t('signup.educationOptions.someCollegeTradeSchool')}</MenuItem>
            <MenuItem value="4">{t('signup.educationOptions.associatesDegree')}</MenuItem>
            <MenuItem value="5">{t('signup.educationOptions.bachelorsDegree')}</MenuItem>
            <MenuItem value="6">
              {t('signup.educationOptions.graduateProfessionalDegree')}
            </MenuItem>
          </TextField>

          <TextField
            margin="normal"
            required
            fullWidth
            label={t('signup.streetAddress')}
            data-cy="street_address-cy"
            defaultValue={patientDraft?.demographics.street_address ?? ''}
            name="street_address"
          />

          <Box className="signup__row3">
            <TextField
              label={t('signup.city')}
              name="city"
              data-cy="city-cy"
              defaultValue={patientDraft?.demographics.city ?? ''}
              required
              className="signup__city"
            />

            <TextField
              label={t('signup.state')}
              name="state"
              data-cy="state-cy"
              required
              value={stateAbbr}
              onChange={handleStateChange}
              inputProps={{ maxLength: 2 }}
              className="signup__state"
            />

            <TextField
              label={t('signup.zip')}
              name="zip"
              data-cy="zipcode-cy"
              required
              value={zip}
              onChange={handleZipChange}
              inputMode="numeric"
              inputProps={{ maxLength: 5, pattern: '\\d{5}' }}
              className="signup__zip"
              helperText={zip && zip.length < 5 ? t('signup.errors.zipHelper') : ' '}
            />
          </Box>

          <Box className="signup__phone" data-cy="phone_number-cy">
            <PhoneInput
              country={'us'}
              value={phone}
              onChange={setPhone}
              containerClass="signup__phoneContainer"
              inputClass="signup__phoneInput"
            />
          </Box>

          <Button type="submit" fullWidth variant="contained" className="signup__submit" data-cy="signup_submit_btn">
            {t('signup.create')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

export default Signup;
